import { CaretRight, SlidersHorizontal } from "@phosphor-icons/react";
import clsx from "clsx";
import { Fragment, type JSX, useState } from "react";

import BadgeWrapper from "common/components/BadgeWrapper";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { Tag } from "common/components/ui/Tag";
import { typedObjectEntries, typedObjectKeys } from "common/helpers/utils";

const filterItemClassName = "px-2 py-2.5 flex items-center gap-x-1.5";

export default function MobileFiltersDrawer<
  Filters extends Record<
    string,
    { label: string; icon: JSX.Element; isBoolean?: true }
  >,
>({
  title,
  filters,
  renderFilter,
  onClear,
  getSelectionCount,
}: {
  title: string;
  filters: Filters;
  renderFilter: (filterKey: keyof Filters) => JSX.Element;
  onClear: (type: keyof Filters | "all") => void;
  //returns the number of selected facets for the key
  getSelectionCount: (filterKey: keyof Filters) => number;
}) {
  const [selectedKey, setSelectedKey] = useState<keyof Filters>();

  const selectedKeySelectionCount = selectedKey
    ? getSelectionCount(selectedKey)
    : undefined;

  const totalSelectionCount = typedObjectKeys(filters).reduce(
    (accumulator, key) => {
      return getSelectionCount(key) + accumulator;
    },
    0,
  );

  return (
    <Drawer
      onAfterClose={() => {
        setSelectedKey(undefined);
      }}
    >
      <BadgeWrapper
        badgeClassName={clsx(
          "bg-purple-600 h-4 min-w-4 top-0 right-0 px-1",
          !totalSelectionCount && "hidden",
        )}
        value={totalSelectionCount}
      >
        <DrawerTrigger asChild>
          <Button size="lg" intent="iconOnly" variant="outline-black">
            <SlidersHorizontal />
          </Button>
        </DrawerTrigger>
      </BadgeWrapper>
      <DrawerContent>
        <DrawerHeader
          title={selectedKey ? filters[selectedKey].label : title}
          onBack={selectedKey && (() => setSelectedKey(undefined))}
          actionComponent={
            <RenderIf
              condition={selectedKey ? !!selectedKeySelectionCount : true}
            >
              <Button
                variant="link-danger"
                size="md"
                onClick={() =>
                  onClear(
                    selectedKey && selectedKeySelectionCount
                      ? selectedKey
                      : "all",
                  )
                }
              >
                Clear
              </Button>
            </RenderIf>
          }
        />
        <DrawerBody>
          {selectedKey
            ? renderFilter(selectedKey)
            : typedObjectEntries(filters).map(
                ([key, { icon, label, isBoolean }]) => {
                  const selectionCount = getSelectionCount(key);
                  const itemContent = (
                    <>
                      <div className="flex-1 flex gap-x-1.5 text-button-sm [&_svg]:size-5">
                        {icon}
                        {label}
                        <RenderIf condition={!isBoolean && !!selectionCount}>
                          <Tag size="sm">{selectionCount}</Tag>
                        </RenderIf>
                      </div>
                      {/* boolean filters are rendered directly, they don't have filter selection */}
                      {isBoolean ? (
                        renderFilter(key)
                      ) : (
                        <CaretRight className="size-4" />
                      )}
                    </>
                  );
                  return (
                    <Fragment key={key as string}>
                      {isBoolean ? (
                        <div className={filterItemClassName}>{itemContent}</div>
                      ) : (
                        <button
                          className={filterItemClassName}
                          type="button"
                          onClick={() => setSelectedKey(key)}
                        >
                          {itemContent}
                        </button>
                      )}
                      <div className="h-px my-1 bg-black-200 last:hidden" />
                    </Fragment>
                  );
                },
              )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
